import React from 'react';
import { isModuleRegistryTypeName, moduleRegistry, type ModuleRegistryTypeName } from './moduleRegistry';
import type { EnrichedModule, EnrichedModuleByTypeName } from './propInjection';
import { hasSchema } from './type-guards';
import Schema from 'components/shared/Schema/Schema';

interface UnknownModule {
  __typename: string;
  schema?: {
    jsonLd?: object;
  };
}

interface ModuleRendererProps {
  module: EnrichedModule | UnknownModule;
  /** Pre-rendered children for container modules (e.g. PageSection) */
  children?: React.ReactNode;
}

function isRegisteredModule(module: ModuleRendererProps['module']): module is EnrichedModule {
  return isModuleRegistryTypeName(module.__typename);
}

function renderComponent<T extends ModuleRegistryTypeName>(
  typeName: T,
  module: EnrichedModuleByTypeName<T>,
  children?: React.ReactNode,
) {
  const Component = moduleRegistry[typeName];
  // @ts-expect-error — TS cannot correlate generic registry lookup with JSX spread props
  return <Component {...module}>{children}</Component>;
}

function renderRegisteredModule<T extends ModuleRegistryTypeName>(module: EnrichedModuleByTypeName<T>) {
  return renderComponent(module.__typename, module);
}

function renderPageSection(module: EnrichedModuleByTypeName<'PageSection'>, children?: React.ReactNode) {
  return renderComponent('PageSection', module, children);
}

/**
 * Generic recursive renderer that uses moduleRegistry for all lookups.
 * Zero experiment imports — rendering is fully decoupled from resolution.
 */
export const ModuleRenderer = ({ module, children }: ModuleRendererProps) => {
  if (!isRegisteredModule(module)) {
    return null;
  }

  const renderedModule =
    module.__typename === 'PageSection' ? renderPageSection(module, children) : renderRegisteredModule(module);

  if (hasSchema(module)) {
    return (
      <>
        <Schema {...module.schema} />
        {renderedModule}
      </>
    );
  }

  return renderedModule;
};
